import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { authApi } from '../../features/auth/api';
import { getErrorMessage } from '../../shared/api/client';
import { normalizeRole } from '../../shared/constants/roles';
import { storage } from '../../shared/lib/storage';

export const loginUser = createAsyncThunk('auth/loginUser', async (credentials, { rejectWithValue }) => {
  try {
    const response = await authApi.login(credentials);
    const payload = response.data?.data || response.data;

    return {
      token: payload?.token,
      user: payload?.user,
      role: normalizeRole(payload?.user?.role),
    };
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const bootstrapSession = createAsyncThunk('auth/bootstrapSession', async (_, { rejectWithValue }) => {
  const token = storage.getToken();

  if (!token) {
    return { authenticated: false };
  }

  try {
    const response = await authApi.getProfile();
    const payload = response.data?.data || response.data;

    return {
      authenticated: true,
      token,
      user: payload,
      role: normalizeRole(payload?.role),
    };
  } catch (error) {
    storage.clearSession();

    if (error.response?.status === 401) {
      return { authenticated: false };
    }

    return rejectWithValue(getErrorMessage(error));
  }
});

export const fetchCapabilities = createAsyncThunk(
  'auth/fetchCapabilities',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authApi.getCapabilities();
      return response.data?.data || response.data || {};
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

const storedSession = storage.readSession();

const initialCapabilities = {
  status: 'idle',
  permissions: [],
  navigation: [],
  scopes: { platform: false, societies: [], products: [] },
  memberships: [],
  featureFlags: {},
  actor: null,
  error: null,
};

const resetCapabilities = () => ({
  ...initialCapabilities,
  permissions: [],
  navigation: [],
  memberships: [],
  scopes: { platform: false, societies: [], products: [] },
  featureFlags: {},
  actor: null,
});

const getAuthorizationFingerprint = (user) => {
  if (!user) {
    return null;
  }

  return JSON.stringify({
    id: user.id || null,
    role: normalizeRole(user.role),
    societyId: user.society?.id || user.societyId || null,
    managerAccessLevel: user.managerAccessLevel || null,
  });
};

const initialState = {
  token: storedSession.token,
  user: storedSession.user,
  role: normalizeRole(storedSession.user?.role),
  status: storedSession.token ? 'bootstrapping' : 'unauthenticated',
  error: null,
  capabilities: resetCapabilities(),
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.token = null;
      state.user = null;
      state.role = null;
      state.status = 'unauthenticated';
      state.error = null;
      state.capabilities = resetCapabilities();
      storage.clearSession();
    },
    clearAuthError: (state) => {
      state.error = null;
    },
    setSessionUser: (state, action) => {
      const currentAuthzFingerprint = getAuthorizationFingerprint(state.user);
      const nextUser = action.payload || null;
      const nextAuthzFingerprint = getAuthorizationFingerprint(nextUser);

      state.user = nextUser;
      state.role = normalizeRole(nextUser?.role);
      if (currentAuthzFingerprint !== nextAuthzFingerprint) {
        state.capabilities = resetCapabilities();
      }
      storage.persistSession({ token: state.token, user: state.user });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.status = 'authenticating';
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        const { token, user, role } = action.payload;
        state.token = token;
        state.user = user;
        state.role = role;
        state.status = 'authenticated';
        state.error = null;
        state.capabilities = resetCapabilities();
        storage.persistSession({ token, user });
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.status = 'unauthenticated';
        state.error = action.payload || 'Login failed';
        state.capabilities = resetCapabilities();
      })
      .addCase(bootstrapSession.pending, (state) => {
        state.status = state.token ? 'bootstrapping' : 'unauthenticated';
      })
      .addCase(bootstrapSession.fulfilled, (state, action) => {
        if (!action.payload.authenticated) {
          state.token = null;
          state.user = null;
          state.role = null;
          state.status = 'unauthenticated';
          state.error = null;
          state.capabilities = resetCapabilities();
          return;
        }

        state.token = action.payload.token;
        state.user = action.payload.user;
        state.role = action.payload.role;
        state.status = 'authenticated';
        state.error = null;
        state.capabilities = resetCapabilities();
        storage.persistSession({ token: action.payload.token, user: action.payload.user });
      })
      .addCase(bootstrapSession.rejected, (state, action) => {
        state.token = null;
        state.user = null;
        state.role = null;
        state.status = 'unauthenticated';
        state.error = action.payload || 'Session bootstrap failed';
        state.capabilities = resetCapabilities();
      })
      .addCase(fetchCapabilities.pending, (state) => {
        state.capabilities.status = 'loading';
        state.capabilities.error = null;
      })
      .addCase(fetchCapabilities.fulfilled, (state, action) => {
        const payload = action.payload || {};
        state.capabilities = {
          status: 'loaded',
          permissions: Array.isArray(payload.permissions) ? payload.permissions : [],
          navigation: Array.isArray(payload.navigation) ? payload.navigation : [],
          scopes: payload.scopes || { platform: false, societies: [], products: [] },
          memberships: Array.isArray(payload.memberships) ? payload.memberships : [],
          featureFlags: payload.featureFlags || {},
          actor: payload.actor || null,
          error: null,
        };
      })
      .addCase(fetchCapabilities.rejected, (state, action) => {
        state.capabilities = {
          ...resetCapabilities(),
          status: 'error',
          error: action.payload || 'Capabilities unavailable',
        };
      });
  },
});

export const { logout, clearAuthError, setSessionUser } = authSlice.actions;
export default authSlice.reducer;
